import { HierarcicalObjectReference } from "@novorender/data-js-api";
import { MutableRefObject, useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Tree } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { ModelTreeNodeData } from "features/modelTree/types";
import { emptyModelTree, expandNode, loadMore } from "features/modelTree/utils";
import { selectIsOnline, selectIsProcoreDesign } from "slices/explorer";
import { telemetry } from "telemetry";
import { AppObjectDB } from "utils/appObjectDb";
import { isLeafNode } from "utils/objectPath";
import { fetchDescendantsBatch } from "utils/preloadDescendants";

/**
 * Upper bound on how many tree nodes the background preload will build. Bounds
 * network/memory so a huge model doesn't get fully loaded just-in-case, while
 * still covering a large portion of typical models (each folder page brings up
 * to ~500 children). Loading happens one folder page per browser idle slot, so
 * a large budget only means the preload keeps trickling in the background for
 * longer — it never competes with user interaction.
 */
const MAX_PRELOAD_NODES = 5000;

/**
 * Don't descend past this depth during the background preload. The node budget
 * is the primary guard; this just avoids spending the whole budget on a single
 * pathologically deep branch.
 */
const MAX_PRELOAD_DEPTH = 12;

/**
 * Fallback timeout for `requestIdleCallback` so the preload still makes progress
 * on a persistently busy tab, while normally yielding to higher-priority work
 * (e.g. the 3D scene's initial streaming).
 */
const IDLE_TIMEOUT_MS = 2000;

/**
 * One unit of Phase 2 idle work. `structure` loads a folder's children (paginated,
 * possibly re-queued for its remaining pages); `descendants` warms the recursive id
 * list for a batch of already-loaded children so their checkbox/visibility state is
 * available while still collapsed. Both share one FIFO queue so a wide level's worth
 * of structure and descendants work interleave breadth-first, rather than descendants
 * work for an early folder running ahead of structure work for its later siblings.
 */
type PreloadWorkItem =
    | { kind: "structure"; path: string; depth: number }
    | { kind: "descendants"; objects: HierarcicalObjectReference[]; depth: number };

/**
 * Eagerly builds the object tree in the background — before the user first opens
 * the Object Tree panel — and stores it in the provided ref so the panel can
 * seed its initial state from it and render instantly on first open.
 *
 * Two phases:
 *  - Phase 1 (eager): load and expand the root level (top categories) as soon as
 *    the object database is ready.
 *  - Phase 2 (idle): breadth-first, load deeper folders' children during browser
 *    idle time, keeping them collapsed. Folders with more children than one page
 *    (~500) are fully drained, one page per idle slot, before descending into
 *    them. This warms a significant portion of the tree so expanding folders is
 *    instant, without auto-expanding everything or competing with the scene's
 *    initial load. Bounded by {@link MAX_PRELOAD_NODES} and {@link MAX_PRELOAD_DEPTH}.
 *
 * Structure (`children`, i.e. "what are this folder's immediate children") is only
 * half of what a real expand needs — the other half is `descendants` (the full
 * recursive id list each child folder needs to render correct checkbox/visibility
 * state while still collapsed), which {@link usePreloadOnExpand} would otherwise
 * fetch lazily on every expand, even for a folder whose structure was already
 * warmed here. So each breadth-first step that finishes loading a folder's children
 * also enqueues one *more* breadth-first step, at the same depth, to warm those
 * children's `descendants` — interleaved with (not run ahead of) the structural
 * queue, so wide trees still finish a level before descendants work moves on to
 * the next one, matching the same interleaving used for structure itself.
 *
 * Gated to Procore designs and online sessions (the panel is unavailable offline
 * and the underlying search is network-only). Runs once per scene load and
 * aborts on db change / unmount. The ref is mutated (not React state) so the
 * frequent Phase 2 progress updates never trigger re-renders — only the one-shot
 * `onRootReady(true)` call after Phase 1 (see {@link PreloadedTreeRootReadyContext})
 * is reactive, so a consumer that mounts before Phase 1 resolves gets a chance to
 * (re-)seed from the ref instead of being stuck with whatever it saw at its own
 * mount time.
 */
export function useObjectTreePreload(
    treeRef: MutableRefObject<Tree<ModelTreeNodeData> | null>,
    onRootReady: (ready: boolean) => void,
): void {
    const { state } = useExplorerGlobals();
    const db = state?.db;
    const isOnline = useAppSelector(selectIsOnline);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);

    const preloadedDbRef = useRef<AppObjectDB | null>(null);

    useEffect(() => {
        // Scene unloaded — drop any preloaded tree so a later scene doesn't seed
        // its panel from a stale tree.
        if (!db) {
            preloadedDbRef.current = null;
            treeRef.current = null;
            onRootReady(false);
            return;
        }

        if (!isOnline || !isProcoreDesign || preloadedDbRef.current === db) {
            return;
        }

        preloadedDbRef.current = db;
        treeRef.current = null;
        onRootReady(false);

        const abortController = new AbortController();
        void runPreload(db, abortController.signal, isProcoreDesign, treeRef, onRootReady);

        return () => {
            abortController.abort();
        };
    }, [db, isOnline, isProcoreDesign, treeRef, onRootReady]);
}

async function runPreload(
    db: AppObjectDB,
    signal: AbortSignal,
    skipPdfFiles: boolean,
    treeRef: MutableRefObject<Tree<ModelTreeNodeData> | null>,
    onRootReady: (ready: boolean) => void,
): Promise<void> {
    try {
        // ── Phase 1: root level (top categories), expanded ──────────────────
        const rootExpander = expandNode({ tree: emptyModelTree, path: "", db, abortSignal: signal, skipPdfFiles });
        if (!rootExpander.load) {
            return;
        }
        const rootPatch = await rootExpander.load();
        if (signal.aborted) {
            return;
        }
        let currentTree = rootPatch(emptyModelTree, true);
        treeRef.current = currentTree;
        onRootReady(true);

        // ── Phase 2: deeper folders, breadth-first, during idle time ────────
        // Each queue entry is one unit of idle work: a folder's first children
        // page, one of its remaining pages, or a batch of `descendants` fetches
        // for a folder's already-loaded children. A folder only descends into
        // its children once it is fully drained, so preloaded folders never need
        // extra "load more" fetches when the user expands them.
        const queue: PreloadWorkItem[] = [];
        // Paths whose descent has been scheduled, so a folder that is visited
        // multiple times (once per remaining page) enqueues each child only once.
        const scheduled = new Set<string>();
        // Child paths whose `descendants` fetch has been scheduled, so re-visiting
        // a folder (e.g. across its remaining pages) doesn't re-queue the same child.
        const descendantsScheduled = new Set<string>();

        // Mirrors usePreloadOnExpand's targeting exactly: non-leaf children of
        // `path` that don't have `descendants` yet. Queued as one batch so it
        // costs a single idle slot, same granularity as a structure fetch.
        const enqueueDescendants = (path: string, depth: number) => {
            const objects: HierarcicalObjectReference[] = [];
            for (const childPath of currentTree[path]?.children ?? []) {
                const child = currentTree[childPath];
                if (
                    child &&
                    !child.isLeaf &&
                    !isLeafNode(child.data.object) &&
                    child.data.object.descendants === undefined &&
                    !descendantsScheduled.has(childPath)
                ) {
                    descendantsScheduled.add(childPath);
                    objects.push(child.data.object);
                }
            }
            if (objects.length > 0) {
                queue.push({ kind: "descendants", objects, depth });
            }
        };

        const scheduleChildren = (path: string, depth: number) => {
            if (depth >= MAX_PRELOAD_DEPTH) {
                return;
            }
            for (const childPath of currentTree[path].children ?? []) {
                const child = currentTree[childPath];
                if (child && !child.isLeaf && !scheduled.has(childPath)) {
                    scheduled.add(childPath);
                    queue.push({ kind: "structure", path: childPath, depth: depth + 1 });
                }
            }
        };

        enqueueDescendants("", 0);
        scheduleChildren("", 0);

        while (queue.length > 0 && !signal.aborted) {
            if (Object.keys(currentTree).length >= MAX_PRELOAD_NODES) {
                break;
            }

            // Yield to the browser so the preload competes as little as possible
            // with user interaction and the scene's initial streaming.
            await waitForIdle(signal);
            if (signal.aborted) {
                break;
            }

            const item = queue.shift()!;

            if (item.kind === "descendants") {
                try {
                    await fetchDescendantsBatch(db, item.objects, signal);
                } catch (ex) {
                    if ((ex as { name?: string }).name === "AbortError") {
                        return;
                    }
                    // Best-effort: skip this batch and keep preloading the rest.
                }
                continue;
            }

            const { path, depth } = item;
            const node = currentTree[path];
            if (!node || node.isLeaf) {
                continue;
            }

            try {
                if (!node.children) {
                    // First page of this folder's children.
                    const expander = expandNode({ tree: currentTree, path, db, abortSignal: signal, skipPdfFiles });
                    if (expander.load) {
                        const patch = await expander.load();
                        if (signal.aborted) {
                            break;
                        }
                        // relevant=false: load the children but keep the folder collapsed
                        // so the panel opens clean while the data is ready for instant expand.
                        currentTree = patch(currentTree, false);
                    } else {
                        currentTree = expander.tree;
                    }
                    treeRef.current = currentTree;
                } else if (node.hasMoreChildren) {
                    // Folder has remaining pages (> ~500 children) — drain one more
                    // page per idle slot.
                    const loader = loadMore({ tree: currentTree, path, db, abortSignal: signal, skipPdfFiles });
                    if (loader.load) {
                        currentTree = loader.tree;
                        const patch = await loader.load();
                        if (signal.aborted) {
                            break;
                        }
                        currentTree = patch(currentTree, false);
                    } else {
                        currentTree = loader.tree;
                    }
                    treeRef.current = currentTree;
                }
                // else: already fully loaded (e.g. by an interleaved user expansion
                // sharing the same tree map) — just descend below.

                if (currentTree[path]?.hasMoreChildren) {
                    // Re-queue to drain the next page on a later idle slot.
                    queue.push({ kind: "structure", path, depth });
                } else {
                    enqueueDescendants(path, depth);
                    scheduleChildren(path, depth);
                }
            } catch (ex) {
                if ((ex as { name?: string }).name === "AbortError") {
                    return;
                }
                // Best-effort: skip this folder and keep preloading the rest.
            }
        }
    } catch (ex) {
        if ((ex as { name?: string }).name === "AbortError") {
            return;
        }
        // Best-effort: on failure the panel just falls back to loading on demand.
        telemetry.trackCaughtException({
            source: "useObjectTreePreload",
            category: "data",
            error: ex,
        });
    }
}

/**
 * Resolves on the next browser idle period (or after {@link IDLE_TIMEOUT_MS} on a
 * busy tab), and immediately if the signal aborts. Falls back to a short timeout
 * where `requestIdleCallback` is unavailable.
 */
function waitForIdle(signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        if (signal.aborted) {
            resolve();
            return;
        }

        const hasIdleCallback = typeof window !== "undefined" && typeof window.requestIdleCallback === "function";

        const onAbort = () => {
            cancel();
            cleanup();
            resolve();
        };

        let cancel: () => void;
        const cleanup = () => signal.removeEventListener("abort", onAbort);

        if (hasIdleCallback) {
            const id = window.requestIdleCallback(
                () => {
                    cleanup();
                    resolve();
                },
                { timeout: IDLE_TIMEOUT_MS },
            );
            cancel = () => window.cancelIdleCallback?.(id);
        } else {
            const id = setTimeout(() => {
                cleanup();
                resolve();
            }, 50);
            cancel = () => clearTimeout(id);
        }

        signal.addEventListener("abort", onAbort, { once: true });
    });
}
